import { parse as parseDate } from "date-fns";
import { getStore } from "~/shared/store/configureStore";
import { setBackendVersjon } from "@sb1f/app-oppdatert";

// HTTP/2 responses carry an empty `statusText`, so fall back to a descriptor that
// identifies the failing request (status + path) for logging/diagnostics. The query
// string is intentionally dropped to avoid logging any sensitive parameters.
const describeResponse = (response: Response) => {
	try {
		return `HTTP ${response.status} ${new URL(response.url).pathname}`;
	} catch {
		return `HTTP ${response.status}`;
	}
};

class FetchError extends Error {
	status: number;
	details: any;
	response: Response;

	constructor(message: string, status: number, details: any, response: Response) {
		super(message || describeResponse(response));
		this.status = status;
		this.details = details;
		this.response = response;
	}
}

interface Overrides {
	headers?: object;
	responseType?: string;
}

type Verb = "GET" | "POST" | "PUT" | "DELETE";

const removeNullValues = (data) => {
	if (Array.isArray(data)) {
		return data.map(removeNullValues);
	}
	if (!data || typeof data !== "object") {
		return data;
	}

	const copy = { ...data };

	Object.keys(copy).forEach((key) => {
		if (copy[key] === null) {
			delete copy[key];
		}
	});
	return copy;
};

const isError = (response: Response) => response.status >= 400;

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

const mapDates = (obj) => {
	if (Array.isArray(obj)) {
		return obj.map((o) => mapDates(o));
	}
	if (typeof obj !== "object" || obj === null) {
		return obj;
	}
	return Object.entries(obj)
		.map(([key, value]) => {
			if (Array.isArray(value)) {
				return { [key]: value.map((v) => mapDates(v)) };
			}
			if (typeof value === "string" && dateRegex.test(value)) {
				return { [key]: parseDate(value, "yyyy-MM-dd", new Date()).getTime() };
			}
			return { [key]: value };
		})
		.reduce((o, result) => ({ ...o, ...result }), {});
};

const textOrJson = (response: Response) => {
	const contentType = response.headers.get("Content-Type") || "";
	if (contentType.includes("json")) {
		return response
			.json()
			.then((json) => mapDates(json))
			.catch((e) => {
				throw new FetchError(`Error while parsing JSON: ${e}`, response.status, "", response);
			});
	}
	return response.text();
};

const parse = (response: Response) => {
	setBackendVersjon(response.headers.get("x-app-version"));
	if (response.status === 204) {
		return null;
	}
	if (isError(response)) {
		return textOrJson(response).then((error) => {
			throw new FetchError(response.statusText, response.status, error, response);
		});
	}
	return textOrJson(response);
};

const parseOnlyJson = (response: Response) => {
	if (response.status === 204) {
		return null;
	}
	const contentType = response.headers.get("Content-Type") || "";
	if (contentType.includes("json") || isError(response)) {
		return parse(response);
	}
	return response.text().then((body) => {
		if (body === "") {
			return "";
		}
		throw new Error("Expected content-type to contain json");
	});
};

const parseOnlyText = (response: Response) => {
	if (response.status === 204) {
		return null;
	}
	const contentType = response.headers.get("Content-Type") || "";
	if (contentType.includes("text") || isError(response)) {
		return parse(response);
	}
	throw new Error("Expected content-type to contain text");
};

const getHeaders = (verb: Verb) => {
	const height = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
	const width = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
	const headers = {
		Accept: "application/json,text/plain",
		"X-Requested-By": getStore()?.getState()?.customer?.ssn || "aliens",
		Resolution: `${height}x${width}`,
	};

	if (verb !== "GET") {
		headers["Content-Type"] = "application/json";
	}

	return headers;
};

const withGetParameters = (url: string, params = {}) => {
	if (Object.keys(params).length === 0) {
		return url;
	}
	const paramString = Object.entries(params)
		.filter(([, value]) => ![null, undefined, NaN].includes(value as any))
		.map(([key, value]) => `${key}=${value}`)
		.join("&");

	return encodeURI(`${url}?${paramString}`);
};

const doRequest =
	(verb: Verb) =>
	(parser) =>
	(url: string, data = {}, overrides: Overrides = {}) => {
		const defaults: RequestInit = {
			method: verb,
			credentials: "same-origin",
			body: verb !== "GET" && verb !== "DELETE" ? JSON.stringify(data) : undefined,
		};

		const config = {
			...defaults,
			...overrides,
			headers: new Headers({
				...getHeaders(verb),
				...overrides.headers,
			}),
		};

		return fetch(url, config).then(parser).then(removeNullValues);
	};

export function get(url: string, data, overrides?) {
	return doRequest("GET")(parse)(withGetParameters(url, data), {}, overrides);
}

export function getJson(url: string, data, overrides?) {
	return doRequest("GET")(parseOnlyJson)(withGetParameters(url, data), {}, overrides);
}

export function getText(url: string, data, overrides?) {
	return doRequest("GET")(parseOnlyText)(withGetParameters(url, data), {}, overrides);
}

export function post(url: string, data, overrides?) {
	return doRequest("POST")(parse)(url, data, overrides);
}

export function put(url: string, data, overrides?) {
	return doRequest("PUT")(parse)(url, data, overrides);
}

export function del(url: string, overrides?) {
	return doRequest("DELETE")(parse)(url, {}, overrides);
}
